import { MenuItem, Stack, TextField, useTheme, useMediaQuery } from "@mui/material";
import { uniqBy } from "../../../utils/lodash-replacements";
import * as React from "react";
import { useLocationsState } from "../../../containers/LocationsStateContainer";
import { useTenants } from "../../../hooks/useTenants";
import { Hangar, Location, Ramp, Tenant } from "../../../types";
import { addTenantConformationText } from "../../../utils/phrasing";
import { LocationTextField } from "../../../widgets/LocationTextField";
import { SelectTailNumber } from "../../../widgets/SelectTailNumber";
import { Aircraft } from "../../../types";
import { AutocompleteAircraft } from "./AutocompleteAircraft";

type Props = {
  disableAutocomplete: boolean;
  tailNumber: string;
  setTailNumber: (tailNumber: string) => void;
  owner: string;
  setOwner: (owner: string) => void;
  location: Location<Hangar | Ramp>;
  setShowConfirmation: (message: string) => void;
  type: string;
  setType: (type: string) => void;
  isReference: boolean;
  note: string;
  setNote: (note: string) => void;
  newAircraft: Aircraft;
  setNewAircraft: (aircraft: Aircraft) => void;
  currentLocation: Location<Ramp | Hangar>;
  setCurrentLocation: (l: Location<Ramp | Hangar>) => void;
  assignedLocation: Location<Ramp | Hangar>;
  setAssignedLocation: (l: Location<Ramp | Hangar>) => void;
};

export const NewAircraftForm: React.FC<Props> = ({
  disableAutocomplete = false,
  tailNumber,
  setTailNumber,
  owner,
  setOwner,
  type,
  setType,
  location,
  setShowConfirmation,
  isReference,
  newAircraft,
  setNewAircraft,
  note,
  setNote,
  assignedLocation,
  setAssignedLocation,
  currentLocation,
  setCurrentLocation,
}) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const { tenants } = useTenants();
  const { locations } = useLocationsState();
  const [shouldFlashAircraft, setShouldFlashAircraft] =
    React.useState<boolean>(false);
  const flashTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on component unmount
  React.useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
      }
    };
  }, []);

  // for the time being tail numbers aren't unique. we're going to cheat a bit
  // and dedup on tail number here
  const tenantsWithTailNumber = React.useMemo(
    () =>
      uniqBy(
        tenants.filter((t) => t.tail_number),
        "tail_number"
      ),
    [tenants]
  );

  return (
    <Stack direction="column" spacing={isTablet ? 0.75 : 1}>
      <Stack direction="row" spacing={2}>
        <SelectTailNumber
          disableAutocomplete={disableAutocomplete}
          defaultValue={tailNumber}
          tenants={tenantsWithTailNumber}
          onInputChange={(tailNumber: string) => {
            setTailNumber(tailNumber);
          }}
          onChange={(tenant: Tenant) => {
            // Common setter logic
            setNewAircraft(tenant?.aircraft);
            setType(tenant?.type ?? "");
            setTailNumber(tenant?.tail_number ?? "");
            setOwner(tenant?.owner_manager ?? "");
            setNote(tenant?.note ?? "");

            if (tenant?.aircraft) {
              // Clear any existing timeout before setting a new one
              if (flashTimeoutRef.current) {
                clearTimeout(flashTimeoutRef.current);
              }

              // Trigger flash animation when user selects from tail number dropdown
              setShouldFlashAircraft(true);

              // Reset flash state after the animation completes
              flashTimeoutRef.current = setTimeout(() => {
                setShouldFlashAircraft(false);
                flashTimeoutRef.current = null;
              }, 1500);
            } else {
              // Fallback for cases where aircraft is not available
              setNewAircraft(tenant?.aircraft);
              setType(tenant?.type ?? "");
              setTailNumber(tenant?.tail_number ?? "");
              setOwner(tenant?.owner_manager ?? "");
              setNote(tenant?.note ?? "");
            }
          }}
        />

        <TextField
          inputProps={{ testid: "owner_manager" }}
          fullWidth
          size="small"
          label="Owner/Manager"
          onChange={(evt) => setOwner(evt.target.value)}
          value={owner}
        />
        <TextField
          select
          size="small"
          fullWidth
          label="Type"
          onChange={(evt) => {
            if (evt.target.value === "base") {
              const confirmationText = addTenantConformationText(
                !location || location?.type === "hangar" ? "hangar" : "ramp"
              );
              setShowConfirmation(confirmationText);
              return;
            }
            setType(evt.target.value);
          }}
          value={type}
        >
          {(!location || isReference) && <MenuItem value="base">Base</MenuItem>}
          {(!location || !isReference) && (
            <MenuItem value="transient">Transient</MenuItem>
          )}
        </TextField>
      </Stack>
      <AutocompleteAircraft
        label="Search by make and model"
        onSelect={(aircraft) => setNewAircraft(aircraft)}
        defaultSelected={newAircraft}
        shouldFlash={shouldFlashAircraft}
      />
      <TextField
        margin="dense"
        label="Notes"
        type="text"
        fullWidth
        placeholder="Departure Time: 05/24/2025 12:00 PM"
        multiline
        minRows={isTablet ? 3 : 10}
        onChange={(evt) => setNote(evt.target.value)}
        value={note ?? ""}
      />
      {!location && type === "base" && (
        <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
          <LocationTextField
            isReference={true}
            unassignedText={type === "base" ? "Unassigned" : "N/A"}
            selectedLocationId={assignedLocation?.location_id}
            onChange={(newLocationId) => {
              const newLocation = locations.find(
                (l) => l.location_id === newLocationId
              );
              setAssignedLocation(newLocation);
            }}
          />
          <LocationTextField
            isReference={false}
            selectedLocationId={currentLocation?.location_id}
            unassignedText="Not Hangared"
            onChange={(newLocationId) => {
              const newLocation = locations.find(
                (l) => l.location_id === newLocationId
              );
              setCurrentLocation(newLocation);
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};
